import { Badge, Card, Grid, Group, Text } from "@mantine/core"
import { createColumnHelper } from "@tanstack/react-table"
import React from "react"
import { Link, useNavigate } from "react-router-dom"
import { CreateAgencyOperationsAdminVenueAction, UpdateAgencyOperationsAdminVenueAction } from "src/components/agency/operations/administrator/venues/AgencyOperationsAdminVenueActions"
import { makeEnumBadgeComponent } from "src/components/ui/Badges"
import { DataTable } from "src/components/ui/DataTable"
import { LoadMoreButton } from "src/components/ui/LoadMoreButton"
import { EmptyPageComponent } from "src/components/ui/Misc"
import { ModuleErrors } from "src/components/ui/ModuleErrors"
import { PageFilters, usePageFilters } from "src/components/ui/PageFilters"
import { PageHeader } from "src/components/ui/PageHeader"
import { PageLayout } from "src/components/ui/PageLayout"
import { useActions, useAppState } from "src/state"
import { SetMetaInput } from "src/state/Module"
import { TenantFragment, Tenant_Status_Enum, Tenant_Type_Enum, Tenants_Bool_Exp } from "src/state/gql/_types"
import { TENANT_STATUS_ENUM_PALETTE, TENANT_TYPE_ENUM_PALETTE } from "src/state/palettes"
import { formatDate } from "src/utils/dates"

const TenantStatusBadge = makeEnumBadgeComponent(Tenant_Status_Enum,'filled','lg', TENANT_STATUS_ENUM_PALETTE)
const TenantTypeBadge = makeEnumBadgeComponent(Tenant_Type_Enum,'outline','lg', TENANT_TYPE_ENUM_PALETTE)

export function AgencyOperationsAdminVenues() {

    const navigation = useNavigate()
    const { itemsList, errors, meta, loadingMore, loading, meta: { limit, offset, sortKey, sortOrder } } = useAppState(state => state.tenants)
    const { 
        subscribeToItems, 
        unsubscribeFromItems, 
        getMoreItems, 
        resetModule, 
        setMeta,
    } = useActions().tenants
    const { view } = useAppState(state => state.ui)

    const {
        filters,
        filterParams
    } = usePageFilters<Tenants_Bool_Exp['_and'], TenantFragment>({
        baseFilters: [{
            type: { _neq: Tenant_Type_Enum.Agency }
        }],
        clickFilters: [
            {
                _or: [
                    { status: { _eq: Tenant_Status_Enum.Active } }
                ]
            }
        ],
        searchFilterFields: [],
        searchFilterRelations: {},
        clickFilterField: 'status',
        clickFiltersDisableDefaultAll: true,
        clickFilterDefault: 'active',
        clickFilterValues: {
            active: { label: 'Active', filter: [{ status: { _eq: Tenant_Status_Enum.Active } }] },
            inactive: { label: 'Inactive', filter: [{ status: { _eq: Tenant_Status_Enum.Inactive } }] },
            all: { label: 'All', filter: [] }
        },
        clickFilterSets: [
            {
                field: 'type',
                label: 'Venue Type',
                default: 'all',
                placeholder: 'Select Venue Type',
                disableAll: true,
                values: {
                    'all': { label: 'All Venue Types', filter: [] },
                    'bar': { label: 'Bar', filter: [{ type: { _eq: Tenant_Type_Enum.Bar } }] },
                    'business': { label: 'Business', filter: [{ type: { _eq: Tenant_Type_Enum.Business } }] },
                    'club': { label: 'Club', filter: [{ type: { _eq: Tenant_Type_Enum.Club } }] },
                    'event_space': { label: 'Event Space', filter: [{ type: { _eq: Tenant_Type_Enum.EventSpace } }] },
                    'hotel': { label: 'Hotel', filter: [{ type: { _eq: Tenant_Type_Enum.Hotel } }] },
                    'restaurant': { label: 'Restaurant', filter: [{ type: { _eq: Tenant_Type_Enum.Restaurant } }] },
                }
            }
        ]
    })

    React.useEffect(() => {
        subscribeToItems(filters);
        return () => {
            unsubscribeFromItems()
        }
    }, [filters, limit, offset, sortKey, sortOrder, subscribeToItems, unsubscribeFromItems, resetModule])

    const columnHelper = createColumnHelper<TenantFragment>()

    const columns = React.useMemo(() => [
        columnHelper.accessor('id', {
            header: 'Id'
        }),
        columnHelper.accessor('tenant_number', {
            header: '#'
        }),
        columnHelper.accessor(row => row.tenant_status, {
            id: 'status',
            meta: {
                relationKey: 'thread_status',
                sortKey: 'value'
            },
            header: 'Status',
            cell: row => <TenantStatusBadge status={row.getValue()!} />
        }),
        columnHelper.accessor(row => row.tenant_type, {
            id: 'type',
            meta: {
                relationKey: 'tenant_type',
                sortKey: 'value'
            },
            header: 'Type',
            cell: row => <TenantTypeBadge status={row.getValue()!} />
        }),
        columnHelper.accessor('name', {
            header: 'Name'
        }),
        columnHelper.accessor('city', {
            header: 'City'
        }),
        columnHelper.accessor('state', {
            header: 'State'
        }),
        columnHelper.accessor('phone', {
            header: 'Phone'
        }),
        columnHelper.accessor('created_at', {
            header: 'Created At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
        columnHelper.accessor('updated_at', {
            header: 'Updated At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
    ], [columnHelper])

    const initialState = {
        columnVisibility: {
            id: false
        }
    }

    const handleMetaChange = (meta: SetMetaInput<TenantFragment>) => {
        setMeta(meta)
    }

    const handleSelect = (id: string) => {
        navigation(`/venues/${id}`)
    }

    const renderCards = () => {
        return (
            <React.Fragment>
                <Grid>
                    {itemsList.map((i, k) => {
                        return (
                            <Grid.Col span={6} key={k}>
                                <Card
                                    component={Link}
                                    to={`/venues/${i.id}`}
                                    shadow="xs"
                                    className="card-theme-global"
                                >
                                    <Group justify='space-between'>
                                        <Text fw={500}>#{i.id}</Text>
                                        <Badge variant="outline">{i.status}</Badge>
                                    </Group>
                                    <Text size="sm">{i.id}</Text>
                                </Card>
                            </Grid.Col>
                        )
                    })}
                </Grid>
                <LoadMoreButton meta={meta} onLoadMore={getMoreItems} loading={loadingMore} />
            </React.Fragment>
        )
    }

    const renderList = () => {
        return (
            <DataTable<TenantFragment>
                columns={columns}
                data={itemsList}
                loading={loading}
                meta={meta}
                onMetaChange={handleMetaChange}
                initialState={initialState}
                onSelectItem={handleSelect}
            />
        )
    }

    return (
        <PageLayout
            header={
                <PageHeader
                    title={`Venues`}
                    renderAction={
                        <Group>
                            <UpdateAgencyOperationsAdminVenueAction />
                            <CreateAgencyOperationsAdminVenueAction/>
                        </Group>
                    }
                />
            }
        >
            <PageFilters options={filterParams} />
            <ModuleErrors errors={errors} />
            {itemsList.length ? (<> {view === 'list' ? renderList() : renderCards()} </>) : (<EmptyPageComponent label='Venues' />)}
        </PageLayout>
    )

}